/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Pavel Režňák (reznak@cadwork.cz)
 *
 */

import AbstractModule from "../abstract_module";
import {RenderDocument, Utils, Debug} from "js_renderer";
import {mat4, vec3, vec2} from "gl-matrix";
import IHWCameraSimpleModule from "./scene_hw_camera_simple_interface";
import {EHWCameraErrors} from "./scene_hw_camera_simple_enums";
import Scene from "../../scene";

interface IConstraints {
    audio: any;
    video: any;
}

/**
 * Instascan library is loaded dynamically!
 *
 * @param {cadwork.webgl.SceneAPI.Scene} aSceneAPI
 * @constructor
 */
export default
class HWCameraSimpleModule extends AbstractModule implements IHWCameraSimpleModule {

    protected static _typeName: string = "hwcamerasimple";

    private _video: any;
    private _enabled: boolean;
    private _ready: boolean;

    private _stream: any;
    private _initSuccessfullCB: any;

    private _activeDeviceIndex: number;

    private _constraints: IConstraints;

    private _devices: any[];
    private _videoDevicesIds: number[];
    private _mediaDeviceInfos: any;

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        this._type = HWCameraSimpleModule._typeName;

        /** @type HTMLVideoElement */
        this._video = null;
        this._enabled = false;
        this._ready = false;

        this._stream = null;
        this._initSuccessfullCB = null;

        // Get supported devices from WebRTC
        this.clear();

        this._activeDeviceIndex = -1;

        // default constraints should get back facing camera
        this._constraints = {
            audio: false,
            video: {facingMode: "environment"}
        };

        // detect if facing mode constraint is supported
        // let supportedConstraints = navigator.mediaDevices.getSupportedConstraints();
        // this._facingModeSupported = supportedConstraints["facingMode"];
        // console.log(supportedConstraints);
        // console.log('facingMode: ' + this._facingModeSupported);
    }

    public static getClassType(): string {
        return HWCameraSimpleModule._typeName;
    }

    public viewChanged() {
    }

    public viewResized(aContentSize: vec2) {
    }

    /**
     * @return {boolean}
     */
    public isSupported(): boolean {
        return !!(navigator && navigator.mediaDevices && navigator.mediaDevices.enumerateDevices);
    }

    /**
     * @return {boolean}
     */
    public isEnabled(): boolean {
        return this._enabled;
    }

    /**
     * @return {boolean}
     */
    public isReady(): boolean {
        return this._ready;
    }

    /**
     * Prepare this module.
     *
     * @return {boolean} true if already init (and callback will NOT be called)
     */
    public init(aCB: any) {
        this.clear();
        this._ready = false;
        this._enabled = false;
        this._initSuccessfullCB = aCB;
    }

    /**
     * Set video element, where camera will be rendered
     *
     * @param {HTMLVideoElement} videoElement
     */
    public setVideoElement(videoElement: any) {
        // Release old if any
        if (this._video) {
            this._video.pause();
            this._video.srcObject = null;
        }

        this._video = videoElement;

        // Add events
        let that = this;
        this._video.autoplay = true;
        Utils.addEvent(this._video, 'canplay', function() {
            that._ready = true;
            that.sceneAPI.viewModeModule.scheduleRedraw();
        });
        Utils.addEvent(this._video, 'emptied', function() {
            that._ready = false;
            that.sceneAPI.viewModeModule.scheduleRedraw();
        });
    }

    public isAvailable(): boolean {
        return this._video !== null;
    }

    /**
     * @return {HTMLVideoElement}
     */
    public getVideoElement(): HTMLVideoElement {
        // Internal video element
        if (this._video === null) {
            let that = this;
            this._video = document.createElement('video');
            this.sceneAPI.getCanvasParentTag().appendChild(this._video);
            this._video.setAttribute('id', 'cwHiddenVideo');
            // TB: because of iOS devices this element must be on screen, but is hidden behind rendering canvas using z-index
            this._video.setAttribute('style', "position: absolute; left: 4em; top: 4em; z-index: -2000; border: 1px solid white;"); // z-index: -2000;
            this._video.setAttribute('autoplay', "");
            this._video.setAttribute('playsinline', "");

            Utils.addEvent(this._video, 'canplay', function() {

                let width = 300;
                let height = 200;
                // height = video.videoHeight / (video.videoWidth/width);

                that._video.setAttribute('width', width);
                that._video.setAttribute('height', height);

                that._ready = true;
                that.sceneAPI.viewModeModule.scheduleRedraw();
            });

            Utils.addEvent(this._video, 'emptied', function() {
                that._ready = false;
                that.sceneAPI.viewModeModule.scheduleRedraw();
            });
        }

        return this._video;
    }

    /**
     * Async! When done, cbFunction(devices) is called.
     *
     * @param {string} cbFunction
     * @return {array} Array of camera objects: {name: string, id: number}
     */
    public rescanVideoDevices(cbFunction: any) {
        let that = this;

        navigator.mediaDevices.enumerateDevices().then(
            function(deviceInfos) {
                that._devices = [];
                that._videoDevicesIds = [];
                that._mediaDeviceInfos = deviceInfos; // cache

                for (let i = 0; i !== that._mediaDeviceInfos.length; ++i) {
                    let deviceInfo = that._mediaDeviceInfos[i];

                    if (deviceInfo.kind === 'videoinput') {
                        that._videoDevicesIds.push(i);
                        that._devices.push({
                            'name': deviceInfo.label || 'camera ' + (that._devices.length + 1),
                            'id': i
                        });
                    }
                }
                if (that._videoDevicesIds.length === 0) {
                    that._videoDevicesIds = null;
                    if (that._initSuccessfullCB) {
                        that._initSuccessfullCB(EHWCameraErrors.NOVIDEODEVICES);
                    }
                }
                if (cbFunction) {
                    cbFunction(that._devices);
                }
            }
        )
            .catch(that.handleError);
    }

    public gotDevices(deviceInfos: any) {
        this._devices = [];
        for (let i = 0; i !== deviceInfos.length; ++i) {
            let deviceInfo = deviceInfos[i];
            // option.value = deviceInfo.deviceId;
            if (deviceInfo.kind === 'audioinput') {
                // option.text = deviceInfo.label ||
                //     'microphone ' + (audioInputSelect.length + 1);
                // audioInputSelect.appendChild(option);
            } else if (deviceInfo.kind === 'audiooutput') {
                // option.text = deviceInfo.label || 'speaker ' +
                //     (audioOutputSelect.length + 1);
                // audioOutputSelect.appendChild(option);
            } else if (deviceInfo.kind === 'videoinput') {
                this._devices[deviceInfo.deviceId] = deviceInfo.label || 'camera ' + (this._devices.length + 1);
                // option.text = deviceInfo.label || 'camera ' + (videoSelect.length + 1);
                // videoSelect.appendChild(option);
            } else {
                // console.log('Some other kind of source/device: ', deviceInfo);
            }
        }
    }

    public gotStream(aStream) {
        let that = this;

        let videoTracks = aStream.getVideoTracks();
        Debug.logInfo('Using video device: ' + videoTracks[0].label);
        aStream.oninactive = function() {
            that.stop();
        };

        this._stream = aStream;
        if (this._video === null) {
            this.getVideoElement();
        }
        this._video.srcObject = aStream;

        for (let t in videoTracks) {
            let track = videoTracks[t];
            let tracSettings = track.getSettings();

            for (let d = 0; d < this._devices.length; d++) {
                let device = this._devices[d];
                if (tracSettings.deviceId === this._mediaDeviceInfos[device.id].deviceId) {
                    this._activeDeviceIndex = d;
                    Debug.logInfo("Using video device[" + d + "]: " + device.name + " / " + device.id);
                    break;
                }
            }
        }
        this._enabled = true;

        if (this._initSuccessfullCB) {
            this._initSuccessfullCB(EHWCameraErrors.NONE);
        }
    }

    public handleError(error) {
        Debug.logError(error.message);
        let cwErrorCode = EHWCameraErrors.OTHER;
        if (error.name === 'ConstraintNotSatisfiedError') {
            Debug.logError('The resolution ' + this._constraints.video.width.exact + 'x' +
                this._constraints.video.width.exact + ' px is not supported by your device.');
            cwErrorCode = EHWCameraErrors.CONSTRAINTS;
        } else if (error.name === 'OverconstrainedError') {
            Debug.logError('Device constraints too complicated');
            cwErrorCode = EHWCameraErrors.CONSTRAINTS;
        } else if (error.name === 'PermissionDeniedError') {
            Debug.logError('Permissions have not been granted to use your camera and ' +
                'microphone, you need to allow the page access to your devices in ' +
                'order for the demo to work.');
            cwErrorCode = EHWCameraErrors.PERMISSIONS;
        } else if (error.name === 'NotReadableError') {
            Debug.logError('Device not readable');
            cwErrorCode = EHWCameraErrors.ALREADYUSED;
        } else if (error.name === 'NotAllowedError') {
            Debug.logError('User did not granted access to camera');
            this._ready = false;
            cwErrorCode = EHWCameraErrors.PERMISSIONS;
        } else {
            Debug.logError('getUserMedia error: ' + error.name, error);
        }

        if (this._initSuccessfullCB) {
            this._initSuccessfullCB(cwErrorCode);
        }
    }

    /**
     * @return {Array}
     */
    public getDevices(): any[] {
        return this._devices;
    }

    /**
     * @return {object | null}
     */
    public getActiveDevice(): any {
        if (this._activeDeviceIndex < 0 || !this._enabled) {
            return null;
        }
        return this._devices[this._activeDeviceIndex];
    }

    public getDefaultDevice() {

        let videoConstraints: any = {};
        videoConstraints.facingMode = 'environment';

        let constraints: IConstraints = {
            video: videoConstraints,
            audio: false
        };

        navigator.mediaDevices.getUserMedia(constraints)
            .then(this.gotStream.bind(this))
            .catch(this.handleError.bind(this));
    }

    /**
     * Async! Enable camera, if already enabled, disable old one and asynchronously start new.
     * You should set listener before this to get "onStart" event.
     *
     * @param {number} [index = 0]
     */
    public enable(index: number) {
        if (this._enabled) {
            this.disable(this.enable.bind(this, index));
            return;
        }

        if (this._devices.length === 0 && this._videoDevicesIds !== null) { // rescan only after initialization
            navigator.mediaDevices.enumerateDevices().then(this.gotDevices.bind(this));
            this.rescanVideoDevices(this.enable.bind(this, index));
            return;
        } else {
            if (index > this._devices.length) {
                Debug.logWarning("Unable to enable camera '" + index + "'. Camera not found!");
                return;
            }
        }

        if (index < 0) { // initialization - prefer back facing camera if available
            if (this._devices.length === 1) {
                index = 0;
            } else {
                this.getDefaultDevice();
                return;
            }
        }

        // get exact camera device
        this._constraints = {
            audio: false,
            video: {deviceId: {exact: this._mediaDeviceInfos[this._devices[index].id].deviceId}}
        };

        // Get camera deviceId and get video stream from that device
        let device = this._devices[index];
        if (!device) {
            Debug.logError("Non-existing camera on index " + index);
            return;
        }

        navigator.mediaDevices.getUserMedia(this._constraints).then(this.gotStream.bind(this)).catch(this.handleError.bind(this));
    }

    /**
     * Async! Disable camera and if done, optionally call callback function on object with data.
     *
     */
    public disable(aCB: any) {
        if (!this._enabled) {
            Debug.logInfo("Camera is already disabled");
            return;
        }

        this._enabled = false;
        this._activeDeviceIndex = -1;

        this.stop();

        if (aCB) {
            aCB();
        }
    }

    public stop() {
        if (this._stream) {
            this._stream.getTracks().forEach(function (track) {
                track.stop();
            });
        }
        this._ready = false;
        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    /**
     * Clear last scan.
     */
    private clear() {
        this._devices = [];
        this._videoDevicesIds = [];
        this._mediaDeviceInfos = {};
    }

}

Scene.registerSceneAPIModule(HWCameraSimpleModule);
